import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, booleanAttribute, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';

import { TranslatePipe } from '@ngx-translate/core';

import { DateAdapter, ErrorStateMatcher, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { LILO_DATE_FORMATS, LiloDateAdapter } from './lilo-date-adapter';

/** Which flavour of picker to render. */
export type DatepickerType = 'date' | 'range';

/** Startup calendar view for the wrapped `mat-datepicker`. */
export type DatepickerStartView = 'month' | 'year' | 'multi-year';

/** Serialization of the value written back to the bound form control (single-date mode). */
export type DatepickerValueFormat = 'date' | 'iso';

/**
 * Shared datepicker. One place to keep datepicker behavior consistent across apps.
 *
 * - `type="date"` (default): single-date field. A `ControlValueAccessor` wrapping
 *   `mat-form-field` + `matInput` + `mat-datepicker`; bind with `formControlName` / `[formControl]`.
 *   The control holds a native `Date` by default; set `[valueFormat]="'iso'"` to store an ISO string.
 * - `type="range"`: two side-by-side date fields for a start/end pair. To keep host form shape
 *   untouched, it does NOT act as a CVA — pass the two existing controls via `[startControl]` /
 *   `[endControl]`. Set `[linkEndToStart]="true"` to constrain the end field to `>= start`.
 */
@Component({
  selector: 'shared-datepicker',
  standalone: true,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatIcon, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  providers: [
    { provide: DateAdapter, useClass: LiloDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: LILO_DATE_FORMATS },
  ],
  template: `
    @if (type() === 'range') {
      <div class="{{ containerClass() }}">
        <mat-form-field class="w-full flex-1" [appearance]="appearance()">
          @if (fromLabelKey()) {
            <mat-label>{{ fromLabelKey()! | translate }}</mat-label>
          }
          <input matInput [formControl]="startControl()!" [matDatepicker]="startPicker" [min]="minDate()" [max]="maxDate()" />
          <mat-datepicker-toggle matIconSuffix [for]="startPicker" />
          <mat-datepicker #startPicker [touchUi]="touchUi()" [startView]="startView()" />
          @if (startControl()!.hasError('matDatepickerParse')) {
            <mat-error>{{ 'validation.error.incorrectDate' | translate }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field class="w-full flex-1" [appearance]="appearance()">
          @if (toLabelKey()) {
            <mat-label>{{ toLabelKey()! | translate }}</mat-label>
          }
          <input matInput [formControl]="endControl()!" [matDatepicker]="endPicker" [min]="endMin()" [max]="maxDate()" />
          <mat-datepicker-toggle matIconSuffix [for]="endPicker" />
          <mat-datepicker #endPicker [touchUi]="touchUi()" [startView]="startView()" />
          @if (endControl()!.hasError('matDatepickerParse')) {
            <mat-error>{{ 'validation.error.incorrectDate' | translate }}</mat-error>
          }
        </mat-form-field>
      </div>
    } @else {
      <mat-form-field class="w-full" [appearance]="appearance()">
        @if (labelKey()) {
          <mat-label>{{ labelKey()! | translate }}</mat-label>
        }
        <input
          matInput
          [formControl]="control"
          [matDatepicker]="picker"
          [min]="minDate()"
          [max]="maxDate()"
          [required]="required()"
          [readonly]="readonlyInput()"
          [errorStateMatcher]="errorMatcher"
          (click)="onInputClick(picker)"
          (blur)="markTouched()" />
        <mat-datepicker-toggle matIconSuffix [for]="picker">
          @if (toggleIcon()) {
            <mat-icon matDatepickerToggleIcon>{{ toggleIcon() }}</mat-icon>
          }
        </mat-datepicker-toggle>
        <mat-datepicker #picker [touchUi]="touchUi()" [startView]="startView()" [startAt]="startAt()" (closed)="markTouched()" />
        @if (hasParseError()) {
          <mat-error>{{ 'validation.error.incorrectDate' | translate }}</mat-error>
        } @else if (errorKey()) {
          <mat-error>{{ errorKey()! | translate }}</mat-error>
        }
      </mat-form-field>
    }
  `,
})
export class DatepickerComponent implements ControlValueAccessor, OnInit {
  readonly type = input<DatepickerType>('date');

  // Shared inputs
  readonly min = input<Date | string | null>(null);
  readonly max = input<Date | string | null>(null);
  readonly startView = input<DatepickerStartView>('month');
  readonly touchUi = input(false, { transform: booleanAttribute });
  readonly appearance = input<MatFormFieldAppearance>('outline');

  // Single-date (`type="date"`) inputs
  /** Translation key for the field label. Omit to render no label. */
  readonly labelKey = input<string>();
  readonly startAt = input<Date | null>(null);
  /** Material Symbols name for a custom toggle icon; omit to use the Material default toggle. */
  readonly toggleIcon = input<string>();
  /** Open the calendar when the text field itself is clicked. */
  readonly openOnClick = input(false, { transform: booleanAttribute });
  /** Keep the text field read-only (value picked via the calendar only). */
  readonly readonlyInput = input(false, { transform: booleanAttribute });
  readonly required = input(false, { transform: booleanAttribute });
  readonly valueFormat = input<DatepickerValueFormat>('date');
  /** Translation key shown when the control is in an error state. Omit to render no error. */
  readonly errorKey = input<string | undefined>('validation.error.required');

  // Range (`type="range"`) inputs. Typed loosely so both nullable and `nonNullable`
  // Date controls from host forms can be passed without casts.
  readonly startControl = input<FormControl<Date> | FormControl<Date | null>>();
  readonly endControl = input<FormControl<Date> | FormControl<Date | null>>();
  readonly fromLabelKey = input<string>();
  readonly toLabelKey = input<string>();
  /** Constrain the end field's minimum to the currently selected start date. */
  readonly linkEndToStart = input(false, { transform: booleanAttribute });
  /** Layout class for the two-field wrapper. */
  readonly containerClass = input('flex gap-4');

  private readonly ngControl = inject(NgControl, { optional: true, self: true });
  private readonly destroyRef = inject(DestroyRef);

  protected readonly control = new FormControl<Date | null>(null);
  protected readonly minDate = computed(() => this.toDate(this.min()));
  protected readonly maxDate = computed(() => this.toDate(this.max()));

  private readonly startValue = signal<Date | null>(null);
  protected readonly endMin = computed(() => (this.linkEndToStart() ? (this.startValue() ?? this.minDate()) : this.minDate()));

  private readonly touched = signal(false);
  private readonly invalid = signal(false);

  protected readonly errorMatcher: ErrorStateMatcher = { isErrorState: () => this.hasParseError() || (this.touched() && this.invalid()) };

  private onChange: (value: Date | string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    if (this.ngControl) this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.control.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => this.onChange(this.serialize(value)));

    const outer = this.ngControl?.control;
    if (outer) {
      const sync = (): void => {
        this.touched.set(outer.touched);
        this.invalid.set(outer.invalid);
      };
      sync();
      outer.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(sync);
    }

    const start = this.startControl();
    if (start) {
      this.startValue.set(start.value);

      start.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => this.startValue.set(value));
    }
  }

  writeValue(value: Date | string | null): void {
    this.control.setValue(this.toDate(value), { emitEvent: false });
  }

  registerOnChange(fn: (value: Date | string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.control.disable({ emitEvent: false });
    else this.control.enable({ emitEvent: false });
  }

  protected hasParseError(): boolean {
    return this.control.hasError('matDatepickerParse');
  }

  protected markTouched(): void {
    this.touched.set(true);
    this.onTouched();
  }

  protected onInputClick(picker: MatDatepicker<Date>): void {
    if (this.openOnClick()) picker.open();
  }

  private toDate(value: Date | string | null | undefined): Date | null {
    if (!value) return null;
    return value instanceof Date ? value : new Date(value);
  }

  private serialize(value: Date | null): Date | string | null {
    if (this.valueFormat() === 'iso') return value ? value.toISOString() : null;
    return value;
  }
}
